import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import SeoHead from '@/components/seo/SeoHead';

const CAPABILITIES = [
  { title: 'Network-wide Dashboard', description: 'Centralised visibility into clinical adoption, session counts, and documentation metrics across all your commissioned sites.' },
  { title: 'Automated Site Provisioning', description: 'Instantly spin up new, fully-isolated clinical sites for your network with pre-allocated seats and voice minutes.' },
  { title: 'Centralised Team Management', description: 'Easily and securely invite fellow PHN administrators with mandatory MFA enforcement to manage the network together.' },
  { title: 'Governance-first design', description: 'Consent-aware workflows, strict tenant-scoped data separation, and comprehensive audit trails — built in from the start.' },
  { title: 'Implementation support', description: 'Guided onboarding and rollout planning for multi-site commissioned programs.' },
  { title: 'Custom network pricing', description: 'Tailored pricing based on rollout scope, clinician count, governance requirements, and contract term.' },
];

export default function PhnPage() {
  return (
    <PageLayout>
      <SeoHead
        title="PHN Clinical Documentation Platform | Notematic Australia"
        description="Notematic supports Primary Health Networks commissioning AI-assisted clinical documentation — 7 note formats including IAR, SOAP, DAP, BIRP, GIRP, PIRP and PIE — across mental health services. Governance-first, multi-site, custom network pricing."
        canonical="/solutions/phn"
      />
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-white to-secondary-50 dark:from-secondary-900 dark:to-secondary-800">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-950 text-primary-700 dark:text-primary-300 text-sm font-medium mb-8">
            Solutions for Primary Health Networks
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-6">
            Network-wide clinical documentation{' '}
            <span className="text-gradient">for PHNs and commissioned programs</span>
          </h1>
          <p className="text-lg text-secondary-600 dark:text-secondary-300 mb-10">
            For PHNs sponsoring AI-assisted documentation across participating sites,
            Notematic provides a governance-first platform with rollout visibility,
            implementation support, and custom network pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-primary btn-lg">Talk to Us About PHN Rollout</Link>
          </div>
        </div>
      </section>

      {/* PHN context */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
              Built for the way PHNs commission services
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              PHNs are typically the organisational buyer or sponsor, with participating sites
              — MMHCs and other services — as the operational users. Notematic supports this
              model with multi-site architecture, tenant-scoped separation, and structured rollout planning.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {CAPABILITIES.map((cap) => (
              <div key={cap.title} className="rounded-xl bg-secondary-50 dark:bg-secondary-900 border border-secondary-200 dark:border-secondary-700 p-6">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">{cap.title}</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom pricing note */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Custom network pricing
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8">
            PHN and network pricing is tailored based on rollout scope, number of participating
            sites, clinician count, governance requirements, and contract term.
            We can provide a tailored proposal for your program.
          </p>
          <div className="rounded-xl bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-6 text-left space-y-3 mb-8">
            {[
              'Multi-site rollout planning',
              'Automated clinical site provisioning',
              'Centralised PHN Team Management dashboard',
              'Network-wide licensing',
              '5 included auditor seats for compliance oversight',
              'Implementation and onboarding support',
              'Procurement documentation',
              'Custom governance and reporting requirements',
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 text-sm text-secondary-700 dark:text-secondary-300">
                <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {item}
              </div>
            ))}
          </div>
          <Link to="/contact" className="btn btn-primary btn-lg">Request a Tailored Proposal</Link>
        </div>
      </section>

      {/* MMHC link */}
      <section className="py-12 bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <p className="text-secondary-500 dark:text-secondary-400 text-sm">
            Looking for individual centre information?{' '}
            <Link to="/solutions/mmhc" className="text-primary-600 dark:text-primary-400 font-medium hover:underline">
              See our MMHC solutions page →
            </Link>
          </p>
        </div>
      </section>
    </PageLayout>
  );
}

